import {
  Badge,
  Button,
  Card,
  Dialog,
  Disclosure,
  Input,
  Label,
  PageHeader,
  Select,
  Tabs,
  buttonVariants,
  cn,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Archive, Pencil, Plus, X } from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import {
  dateTimeLabel,
  instantFromLocalInput,
  localInputValue,
} from "../dashboard/local-day.js";
import { getRecordTypes } from "../records/records-api.js";
import { todayIn } from "../reports/report-period.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import {
  archiveTaskDefinition,
  cancelOccurrence,
  listTaskDefinitions,
  listTaskOccurrences,
  recurrenceToRRule,
  rruleToFriendly,
  saveTaskDefinition,
  skipOccurrence,
  taskStatusTone,
  type FriendlyRecurrence,
  type TaskDefinition,
  type TaskKind,
  type TaskOccurrence,
} from "./tasks-api.js";
import {
  activePeople,
  assigneeIds,
  assigneeLabel,
  dayHeading,
  dueLabel,
  groupByDate,
  hasNobodyAssigned,
  nobodyAssignedLabel,
  occurrenceStatusLabel,
  recurrenceLabel,
  scheduleStatusLabel,
  showsStatus,
  splitUpcoming,
  taskWindow,
  tasksTab,
  toggleAssignee,
  unassignedSchedules,
  weekdayLabels,
  type PersonChoice,
} from "./tasks-view.js";

const weekdays = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map(
  (code) => [code, weekdayLabels[code] ?? code] as const,
);

function taskRecurrenceLabel(task: TaskDefinition, timezone: string): string {
  if (!task.schedule) return "No schedule";
  if (task.schedule.one_off_due_at) {
    return `Once · ${dateTimeLabel(task.schedule.one_off_due_at, timezone)}`;
  }
  const recurrence = rruleToFriendly(task.schedule.rrule);
  return recurrenceLabel(recurrence.frequency, recurrence.interval, {
    ...(recurrence.frequency === "monthly"
      ? { monthDay: recurrence.monthDay }
      : {}),
    ...(recurrence.frequency === "weekly"
      ? { weekdays: recurrence.weekdays }
      : {}),
  });
}

// "Everyone" follows people as they join or leave the location; naming
// people turns it off.
function WhoDoesThis({
  everyone,
  onChange,
  people,
  selected,
}: {
  everyone: boolean;
  onChange: (change: { everyone: boolean; ids: string[] }) => void;
  people: readonly PersonChoice[];
  selected: readonly string[];
}) {
  const choices = activePeople(people);
  const shown = everyone ? choices.map((person) => person.id) : selected;
  return (
    <fieldset className="sm:col-span-2">
      <legend className="text-sm font-semibold text-stone-800">
        Who does this?
      </legend>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!everyone && selected.length === 0 ? (
          <Badge tone="attention">Nobody assigned</Badge>
        ) : null}
        <Button
          aria-pressed={everyone}
          onClick={() => onChange({ everyone: true, ids: [] })}
          size="sm"
          variant={everyone ? "default" : "outline"}
        >
          Everyone
        </Button>
        {everyone || selected.length > 0 ? (
          <Button
            onClick={() => onChange({ everyone: false, ids: [] })}
            size="sm"
            variant="ghost"
          >
            Clear
          </Button>
        ) : null}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {choices.map((person) => (
          <label
            className={cn(
              "flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border px-3 text-sm font-semibold break-words",
              shown.includes(person.id)
                ? "border-emerald-800 bg-emerald-50 text-emerald-950"
                : "border-stone-200 text-stone-700",
            )}
            key={person.id}
          >
            <input
              checked={shown.includes(person.id)}
              className="size-4 accent-emerald-800"
              onChange={() =>
                onChange({
                  everyone: false,
                  ids: toggleAssignee(shown, person.id),
                })
              }
              type="checkbox"
            />
            {person.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function TaskEditor({
  current,
  locationId,
  onClose,
  onSaved,
  timezone,
}: {
  current?: TaskDefinition;
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
  timezone: string;
}) {
  const id = useId();
  const formId = `${id}-form`;
  const friendly = rruleToFriendly(current?.schedule?.rrule);
  const [scheduleMode, setScheduleMode] = useState<"one_off" | "recurring">(
    current?.schedule?.one_off_due_at ? "one_off" : "recurring",
  );
  const [frequency, setFrequency] = useState<FriendlyRecurrence["frequency"]>(
    friendly.frequency,
  );
  const [taskKind, setTaskKind] = useState<TaskKind>(
    current?.task_kind ?? "log",
  );
  const [assignees, setAssignees] = useState<string[]>(
    current ? assigneeIds(current) : [],
  );
  const [everyone, setEveryone] = useState(
    current ? current.assign_everyone === true : true,
  );
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const people = useMemo<PersonChoice[]>(
    () =>
      (staff.data ?? []).map((person) => ({
        id: person.id,
        name: person.display_name,
        status: person.status,
      })),
    [staff.data],
  );
  const types = useQuery({
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const save = useMutation({
    mutationFn: (form: FormData) => {
      let schedule: Record<string, unknown>;
      if (scheduleMode === "one_off") {
        schedule = {
          one_off_due_at: instantFromLocalInput(
            String(form.get("one_off_due_at")),
            timezone,
          ).toISOString(),
          reminder_offsets_min: [60],
        };
      } else {
        const interval = Number(form.get("interval") ?? 1);
        let recurrence: FriendlyRecurrence;
        if (frequency === "weekly") {
          const selected = form.getAll("weekday").map(String);
          if (selected.length === 0)
            throw new Error("Choose at least one weekday.");
          recurrence = { frequency, interval, weekdays: selected };
        } else if (frequency === "monthly") {
          recurrence = {
            frequency,
            interval,
            monthDay: Number(form.get("month_day") ?? 1),
          };
        } else {
          recurrence = { frequency, interval };
        }
        schedule = {
          active_from: String(form.get("active_from") ?? "") || null,
          active_to: String(form.get("active_to") ?? "") || null,
          due_window_end: String(form.get("due_window_end") ?? "") || null,
          due_window_start: String(form.get("due_window_start") ?? "") || null,
          reminder_offsets_min: [60],
          rrule: recurrenceToRRule(recurrence),
        };
      }
      return saveTaskDefinition(
        locationId,
        {
          ...(everyone
            ? { assign_everyone: true }
            : { assignee_staff_profile_ids: assignees }),
          instructions: String(form.get("instructions") ?? "").trim() || null,
          ...(current ? {} : { origin: "manager" }),
          photo_policy: String(form.get("photo_policy")),
          record_type_code:
            taskKind === "log"
              ? String(form.get("record_type_code") ?? "")
              : null,
          schedule,
          task_kind: taskKind,
          title: String(form.get("title") ?? "").trim(),
        },
        current,
      );
    },
    onSuccess: onSaved,
  });
  const oneOff = current?.schedule?.one_off_due_at
    ? localInputValue(current.schedule.one_off_due_at, timezone)
    : "";

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={save.isPending} form={formId} type="submit">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="lg"
      title={current ? "Edit schedule" : "New schedule"}
    >
      <form
        className="space-y-6"
        id={formId}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          save.mutate(new FormData(event.currentTarget));
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input
              className="mt-1.5"
              defaultValue={current?.title ?? ""}
              id={`${id}-name`}
              name="title"
              required
            />
          </div>
          <div>
            <Label htmlFor={`${id}-kind`}>Type</Label>
            <Select
              id={`${id}-kind`}
              name="task_kind"
              onChange={(event) => setTaskKind(event.target.value as TaskKind)}
              value={taskKind}
              wrapperClassName="mt-1.5"
            >
              <option value="log">Log a record</option>
              <option value="check">Check</option>
              <option value="cleaning">Cleaning</option>
              <option value="maintenance">Maintenance</option>
              <option value="training">Training</option>
              <option value="reminder_follow_up">Reminder follow-up</option>
            </Select>
          </div>
          {taskKind === "log" ? (
            <div>
              <Label htmlFor={`${id}-record`}>Record form</Label>
              <Select
                defaultValue={current?.record_type_code ?? ""}
                id={`${id}-record`}
                name="record_type_code"
                required
                wrapperClassName="mt-1.5"
              >
                <option value="">Choose a record form</option>
                {types.data?.map((type) => (
                  <option key={type.code} value={type.code}>
                    {type.title}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <WhoDoesThis
            everyone={everyone}
            onChange={(change) => {
              setEveryone(change.everyone);
              setAssignees(change.ids);
            }}
            people={people}
            selected={assignees}
          />
          <div>
            <Label htmlFor={`${id}-photo`}>Photo</Label>
            <Select
              defaultValue={current?.photo_policy ?? "optional"}
              id={`${id}-photo`}
              name="photo_policy"
              wrapperClassName="mt-1.5"
            >
              <option value="optional">Optional</option>
              <option value="requested">Ask for a photo</option>
              <option value="required_by_business">Always required</option>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-instructions`}>Instructions</Label>
            <textarea
              className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
              defaultValue={current?.instructions ?? ""}
              id={`${id}-instructions`}
              name="instructions"
            />
          </div>
        </div>

        <section className="space-y-4 border-t border-stone-200 pt-5">
          <h3 className="text-sm font-semibold">When</h3>
          <Tabs
            ariaLabel="When"
            items={[
              { label: "Recurring", value: "recurring" },
              { label: "One-off", value: "one_off" },
            ]}
            onChange={(value) =>
              setScheduleMode(value === "one_off" ? "one_off" : "recurring")
            }
            value={scheduleMode}
            variant="segmented"
          />
          {scheduleMode === "one_off" ? (
            <div>
              <Label htmlFor={`${id}-once`}>Date and time</Label>
              <Input
                className="mt-1.5"
                defaultValue={oneOff}
                id={`${id}-once`}
                name="one_off_due_at"
                required
                type="datetime-local"
              />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor={`${id}-frequency`}>Frequency</Label>
                <Select
                  id={`${id}-frequency`}
                  onChange={(event) =>
                    setFrequency(
                      event.target.value as FriendlyRecurrence["frequency"],
                    )
                  }
                  value={frequency}
                  wrapperClassName="mt-1.5"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Select>
              </div>
              <div>
                <Label htmlFor={`${id}-interval`}>Repeat every</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    defaultValue={friendly.interval}
                    id={`${id}-interval`}
                    min="1"
                    name="interval"
                    required
                    type="number"
                  />
                  <span className="text-xs font-semibold text-stone-500">
                    {frequency === "daily"
                      ? "days"
                      : frequency === "weekly"
                        ? "weeks"
                        : "months"}
                  </span>
                </div>
              </div>
              {frequency === "weekly" ? (
                <div className="sm:col-span-2">
                  <Label>Weekdays</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {weekdays.map(([value, label]) => (
                      <label className="cursor-pointer" key={value}>
                        <input
                          className="peer sr-only"
                          defaultChecked={
                            friendly.frequency === "weekly"
                              ? friendly.weekdays.includes(value)
                              : value === "MO"
                          }
                          name="weekday"
                          type="checkbox"
                          value={value}
                        />
                        <span className="grid size-10 place-items-center rounded-xl border border-stone-300 bg-surface text-xs font-bold peer-checked:border-emerald-800 peer-checked:bg-emerald-800 peer-checked:text-white">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
              {frequency === "monthly" ? (
                <div>
                  <Label htmlFor={`${id}-monthday`}>Day of month</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={
                      friendly.frequency === "monthly" ? friendly.monthDay : 1
                    }
                    id={`${id}-monthday`}
                    max="31"
                    min="1"
                    name="month_day"
                    type="number"
                  />
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2 sm:col-span-2">
                <div>
                  <Label htmlFor={`${id}-window-start`}>Due from</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={current?.schedule?.due_window_start ?? ""}
                    id={`${id}-window-start`}
                    name="due_window_start"
                    type="time"
                  />
                </div>
                <div>
                  <Label htmlFor={`${id}-window-end`}>Due by</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={current?.schedule?.due_window_end ?? ""}
                    id={`${id}-window-end`}
                    name="due_window_end"
                    type="time"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor={`${id}-active-from`}>Starts on</Label>
                <Input
                  className="mt-1.5"
                  defaultValue={
                    current?.schedule?.active_from ?? todayIn(timezone)
                  }
                  id={`${id}-active-from`}
                  name="active_from"
                  type="date"
                />
              </div>
              <div>
                <Label htmlFor={`${id}-active-to`}>Ends on</Label>
                <Input
                  className="mt-1.5"
                  defaultValue={current?.schedule?.active_to ?? ""}
                  id={`${id}-active-to`}
                  name="active_to"
                  type="date"
                />
              </div>
            </div>
          )}
        </section>
        {save.error ? (
          <p
            className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
            role="alert"
          >
            {displayError(save.error) ===
              "Something went wrong. Please try again." &&
            save.error instanceof Error
              ? save.error.message
              : displayError(save.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function Schedules({
  locationId,
  timezone,
}: {
  locationId: string;
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TaskDefinition | "new" | null>(null);
  const tasks = useQuery({
    queryFn: () => listTaskDefinitions(locationId),
    queryKey: ["task-definitions", locationId],
  });
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const names = useMemo(
    () =>
      new Map(
        (staff.data ?? []).map((person) => [person.id, person.display_name]),
      ),
    [staff.data],
  );
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["task-definitions", locationId],
      }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);
  const archive = useMutation({
    mutationFn: archiveTaskDefinition,
    onSuccess: refresh,
  });
  const nobody = unassignedSchedules(tasks.data?.items ?? []).length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {nobody > 0 ? (
            <Badge tone="attention">{nobodyAssignedLabel(nobody)}</Badge>
          ) : null}
        </div>
        <Button onClick={() => setEditing("new")}>
          <Plus className="size-4" /> New schedule
        </Button>
      </div>
      {tasks.isPending ? <LoadingState label="Loading" /> : null}
      {tasks.error ? (
        <ErrorState
          message={displayError(tasks.error)}
          onAction={() => void tasks.refetch()}
          title="Schedules aren’t available"
        />
      ) : null}
      {tasks.data?.items.length === 0 ? (
        <EmptyState title="Add your first schedule" />
      ) : null}
      {tasks.data?.items.length ? (
        <Card className="overflow-hidden">
          <div className="divide-y divide-stone-100">
            {tasks.data.items.map((task) => (
              <div
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5"
                key={task.id}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold break-words">
                      {task.title}
                    </p>
                    {task.status !== "active" ? (
                      <Badge>{scheduleStatusLabel(task.status)}</Badge>
                    ) : null}
                    {task.status === "active" && hasNobodyAssigned(task) ? (
                      <Badge tone="attention">Nobody assigned</Badge>
                    ) : null}
                  </div>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {taskRecurrenceLabel(task, timezone)}
                    {task.assign_everyone
                      ? " · Everyone"
                      : hasNobodyAssigned(task)
                        ? ""
                        : ` · ${assigneeLabel(assigneeIds(task), names)}`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setEditing(task)}
                    size="sm"
                    variant="ghost"
                  >
                    <Pencil className="size-3.5" /> Edit
                  </Button>
                  <Button
                    disabled={archive.isPending}
                    onClick={() => {
                      if (
                        globalThis.confirm(
                          "Archive this schedule? Its future checks are cancelled.",
                        )
                      )
                        archive.mutate(task.id);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <Archive className="size-3.5" /> Archive
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      {archive.error ? (
        <p className="text-sm font-semibold text-red-800">
          {displayError(archive.error)}
        </p>
      ) : null}
      {editing ? (
        <TaskEditor
          {...(editing === "new" ? {} : { current: editing })}
          locationId={locationId}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refresh();
            setEditing(null);
          }}
          timezone={timezone}
        />
      ) : null}
    </div>
  );
}

function TodayAndUpcoming({
  canManage,
  locationId,
  timezone,
}: {
  canManage: boolean;
  locationId: string;
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TaskOccurrence["status"] | "">("");
  const { from, to } = taskWindow(timezone);
  const occurrences = useQuery({
    queryFn: () =>
      listTaskOccurrences(locationId, {
        assignee: canManage ? "any" : "me",
        from,
        ...(status ? { status } : {}),
        to,
      }),
    queryKey: ["task-occurrences", locationId, canManage, status, from],
  });
  const staff = useQuery({
    enabled: canManage,
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const names = useMemo(
    () =>
      new Map(
        (staff.data ?? []).map((person) => [person.id, person.display_name]),
      ),
    [staff.data],
  );
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["task-occurrences", locationId],
      }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);
  const skip = useMutation({
    mutationFn: (id: string) => skipOccurrence(id),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: cancelOccurrence,
    onSuccess: refresh,
  });
  const groups = useMemo(
    () => groupByDate(occurrences.data?.items ?? []),
    [occurrences.data],
  );
  // Today stays in view; later days wait behind one disclosure.
  const { later, today: current } = status
    ? { later: [], today: groups }
    : splitUpcoming(groups, from);
  const laterCount = later.reduce((sum, [, items]) => sum + items.length, 0);

  const renderGroup = ([date, items]: [string, TaskOccurrence[]]) => (
    <section key={date}>
      <h2 className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-xs font-semibold tracking-widest text-stone-500 uppercase sm:px-5">
        {dayHeading(date, from)}
      </h2>
      <div className="divide-y divide-stone-100">
        {items.map((item) => (
          <div
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5"
            key={item.id}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold break-words">
                  {item.title}
                </p>
                {showsStatus(item.status) ? (
                  <Badge tone={taskStatusTone(item.status)}>
                    {occurrenceStatusLabel(item.status)}
                  </Badge>
                ) : null}
                {canManage && hasNobodyAssigned(item) ? (
                  <Badge tone="attention">Nobody assigned</Badge>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-stone-500">
                {dueLabel(item, timezone)}
                {canManage && !hasNobodyAssigned(item)
                  ? ` · ${assigneeLabel(assigneeIds(item), names)}`
                  : ""}
              </p>
            </div>
            {["due", "upcoming", "missed"].includes(item.status) ? (
              <div className="flex items-center gap-1">
                {item.record_type_code ? (
                  <Link
                    className={buttonVariants()}
                    search={{
                      record_type_code: item.record_type_code,
                      task_occurrence_id: item.id,
                      ...(item.asset_id ? { asset_id: item.asset_id } : {}),
                    }}
                    to="/log"
                  >
                    Log it
                  </Link>
                ) : null}
                <Button
                  disabled={skip.isPending}
                  onClick={() => {
                    if (globalThis.confirm("Skip this check?"))
                      skip.mutate(item.id);
                  }}
                  variant="ghost"
                >
                  Skip
                </Button>
                {canManage && item.status !== "missed" ? (
                  <Button
                    aria-label={`Cancel ${item.title}`}
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(item.id)}
                    size="icon"
                    variant="ghost"
                  >
                    <X className="size-4" />
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Select
          aria-label="Status"
          onChange={(event) =>
            setStatus(event.target.value as TaskOccurrence["status"] | "")
          }
          size="sm"
          value={status}
          wrapperClassName="w-44"
        >
          <option value="">All</option>
          <option value="due">Due</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Done</option>
          <option value="missed">Missed</option>
          <option value="skipped">Skipped</option>
          <option value="cancelled">Cancelled</option>
        </Select>
      </div>
      {occurrences.isPending ? <LoadingState label="Loading" /> : null}
      {occurrences.error ? (
        <ErrorState
          message={displayError(occurrences.error)}
          onAction={() => void occurrences.refetch()}
          title="Checks aren’t available"
        />
      ) : null}
      {occurrences.data?.items.length === 0 ? (
        <EmptyState calm title="No checks" />
      ) : null}
      {current.length > 0 ? (
        <Card className="overflow-hidden">{current.map(renderGroup)}</Card>
      ) : null}
      {later.length > 0 ? (
        <Disclosure
          defaultOpen={current.length === 0}
          summary={`Upcoming (${laterCount})`}
        >
          <Card className="mt-2 overflow-hidden">{later.map(renderGroup)}</Card>
        </Disclosure>
      ) : null}
      {skip.error || cancel.error ? (
        <p className="text-sm font-semibold text-red-800" role="alert">
          {displayError(skip.error ?? cancel.error)}
        </p>
      ) : null}
    </div>
  );
}

export function TasksWorkspacePage() {
  const shell = useShell();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const canManage = shell.activeRole !== "staff";
  const location = shell.activeLocation;
  const tab = tasksTab(search.tab, canManage);
  return (
    <>
      <PageHeader className="mb-5" title="Tasks" />
      {!location ? (
        <EmptyState title="Choose a location" />
      ) : (
        <>
          {canManage ? (
            <Tabs
              ariaLabel="Tasks"
              className="mb-5"
              items={[
                { label: "Today & upcoming", value: "today" },
                { label: "Schedules", value: "schedules" },
              ]}
              onChange={(value) =>
                void navigate({
                  search: value === "schedules" ? { tab: "schedules" } : {},
                  to: "/tasks",
                })
              }
              value={tab}
            />
          ) : null}
          {tab === "schedules" ? (
            <Schedules locationId={location.id} timezone={location.timezone} />
          ) : (
            <TodayAndUpcoming
              canManage={canManage}
              locationId={location.id}
              timezone={location.timezone}
            />
          )}
        </>
      )}
    </>
  );
}
